import { useCallback } from "react";
import { AuraBlob } from "../components/AuraBlob";
import { CheckForm } from "../components/CheckForm";
import { ErrorCard } from "../components/ErrorCard";
import { LoadingCard } from "../components/LoadingCard";
import { ResultCard } from "../components/ResultCard";
import { useCheck } from "../hooks/useCheck";
import { useLocalPreferences } from "../hooks/useLocalPreferences";
import { useTurnstileSiteKey } from "../hooks/useRuntimeConfig";
import { executeTurnstile } from "../hooks/useTurnstile";
import { usePageMeta } from "../hooks/usePageMeta";
import type { DesiredTone } from "../types/api";

export function HomePage() {
  usePageMeta(
    "Unc Check | A vibe check for your choices and voices",
    "Drop a habit, text, email, or post. Get a vibe check and a less-unc option."
  );
  const [preferences, setPreferences] = useLocalPreferences();
  const { state, submit, adjustTone, cancel } = useCheck();
  const busy = state.status === "verifying_turnstile" || state.status === "submitting";
  const siteKey = useTurnstileSiteKey();

  const adjust = useCallback(
    (tone: DesiredTone) => {
      adjustTone(tone, () => executeTurnstile(siteKey, document.querySelector(".turnstile-slot")));
    },
    [adjustTone, siteKey]
  );

  return (
    <main className="main-shell">
      <section className="hero" aria-labelledby="hero-heading">
        <div>
          <p className="tagline">a vibe check for your choices and voices</p>
          <h1 id="hero-heading">Is it unc, or are they just hating?</h1>
        </div>
        <AuraBlob />
        <p className="hero-copy">Drop a habit, text, email, or post. Get a vibe check and a less-unc option.</p>
      </section>
      <CheckForm preferences={preferences} setPreferences={setPreferences} siteKey={siteKey} disabled={busy} onSubmit={submit} />
      <div className="result-region" aria-live="polite">
        {busy ? <LoadingCard onCancel={cancel} /> : null}
        {state.status === "error" && state.error ? (
          <ErrorCard
            error={state.error}
            onRetry={
              state.error.retryable
                ? () => {
                    const form = document.querySelector("form");
                    form?.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
                  }
                : undefined
            }
          />
        ) : null}
        {state.status === "success" && state.response ? <ResultCard response={state.response} onAdjust={adjust} /> : null}
      </div>
    </main>
  );
}
